import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect, useRef, useState } from 'react';
import {
    setActiveFloorplan,
    setActiveScene,
    nextStep,
    previousStep,
    jumpToStep,
    toggleAppliedProducts,
    selectCurrentStep
} from '../../../store/visualizerSlice';
import { TopMenu, Visualizer, Floor, Counters, Backsplash, BathFloor, ShowerFloor, ShowerWall, Cabinets, CabinetHandles, KitchenFaucets, Mirrors, BathPlumbing, Paint, KitchenArrangement, ShowerType, BathType, AppliedProducts, RoomTabs, ReviewSelectionsModal } from '../../shared';
import styles from './VisualizerPage.module.css';

export default function VisualizerPage() {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isReviewOpen, setIsReviewOpen] = useState(false);

    // Get URL params
    const { floorplanId } = params;

    // Try to get data from location.state (fresh navigation) or Redux (persisted/refresh)
    const locationState = location.state || {};

    // Redux state
    const { rooms, steps, floorplans, dataLoaded } = useSelector(state => state.floorplan);
    const {
        activeScene,
        activeSteps,
        currentStepIndex,
        showAppliedProducts,
        stepSelections,
        sceneSelStrings,
        sceneSpecifierOverrides,
        activeFloorplanId: persistedFloorplanId
    } = useSelector(state => state.visualizer);

    // Selectors
    const currentStep = useSelector(selectCurrentStep);

    // Reconstruct floorplan from URL params if not in location.state
    const floorplan = useMemo(() => {
        if (locationState.floorplan) return locationState.floorplan;

        if (floorplans.length > 0) {
            // Match on Floorplans.Id
            return floorplans.find(fp =>
                String(fp.Id || fp.id) === String(floorplanId)
            );
        }
        return null;
    }, [locationState.floorplan, floorplans, floorplanId]);

    // Redirect to floorplans page if we have no floorplan after data has loaded
    useEffect(() => {
        if (dataLoaded && !floorplan && !persistedFloorplanId) {
            console.warn('No floorplan found, redirecting to floorplans');
            navigate('/floorplans');
        }
    }, [dataLoaded, navigate]); // Intentionally minimal deps — only check once after data loads

    // Set active floorplan when component mounts, floorplanId changes, or steps arrive
    useEffect(() => {
        if (floorplanId != null && steps.length > 0) {
            const idChanged = String(persistedFloorplanId) !== String(floorplanId);
            const noStepsLoaded = activeSteps.length === 0;
            if (idChanged || noStepsLoaded) {
                console.log('Setting active floorplan from URL:', floorplanId, '(idChanged:', idChanged, ', noStepsLoaded:', noStepsLoaded, ')');
                dispatch(setActiveFloorplan({ floorplanId, steps }));
            }
        }
    }, [floorplanId, steps, dispatch, persistedFloorplanId, activeSteps.length]);

    // Get all rooms for the selected floorplan.
    // Rooms sheet may have multiple rows per Area (e.g. Kitchen has one row per
    // arrangement). We collapse those to one entry per area, preferring the row
    // whose specifier matches the current sceneSpecifierOverrides[area], and
    // falling back to the first row for that area.
    const availableRooms = useMemo(() => {
        if (floorplanId == null || !rooms || rooms.length === 0) return [];

        const byArea = new Map();
        rooms
            .filter(room => String(room.PlanId || room.planId) === String(floorplanId))
            .forEach(room => {
                const area = room.Area || room.area || '';
                if (!area) return;
                const rawSpec = (room.Specifier || room.specifier || '').replace(/\\/g, '/');
                if (!rawSpec) return;

                if (!byArea.has(area)) {
                    byArea.set(area, []);
                }
                byArea.get(area).push({ raw: room, area, spec: rawSpec });
            });

        const result = [];
        for (const [area, entries] of byArea) {
            const override = sceneSpecifierOverrides && sceneSpecifierOverrides[area];
            const picked = (override && entries.find(e => e.spec === override)) || entries[0];
            const spec = override || picked.spec;
            const rawSel = picked.raw.Sel || picked.raw.sel || '-1';
            const sel = String(rawSel);
            const thumbnailUrl = spec
                ? `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(spec)}&sel=${encodeURIComponent(sel)}&type=base&size=508`
                : '';

            result.push({
                id: `${floorplanId}_${area}`,
                planId: picked.raw.PlanId || picked.raw.planId,
                area,
                image: thumbnailUrl,
                specifier: spec,
                sel,
                originalData: picked.raw
            });
        }
        return result;
    }, [floorplanId, rooms, sceneSpecifierOverrides]);

    const initialSceneSet = useRef(false);

    // Set the initial scene on mount — default to Exterior if available, else first room
    useEffect(() => {
        if (availableRooms.length > 0 && !initialSceneSet.current && !activeScene) {
            const exteriorRoom = availableRooms.find(r => r.area?.toLowerCase() === 'exterior');
            const firstRoom = exteriorRoom || availableRooms[0];
            const savedSel = sceneSelStrings?.[firstRoom.area];
            const roomWithSel = savedSel ? { ...firstRoom, sel: savedSel } : firstRoom;
            console.log('Setting initial scene:', firstRoom.area);
            dispatch(setActiveScene(roomWithSel));
            initialSceneSet.current = true;
        }
    }, [availableRooms, activeScene, sceneSelStrings, dispatch]);

    // Step navigation handlers
    const handleNextStep = () => {
        dispatch(nextStep());
    };

    const handlePreviousStep = () => {
        dispatch(previousStep());
    };

    const handleViewAllApplied = () => {
        console.log('View all applied products');
        console.log('Selections:', stepSelections);
        dispatch(toggleAppliedProducts(true));
    };

    const handleCloseAppliedProducts = () => {
        dispatch(toggleAppliedProducts(false));
    };

    // Room tab click: jump to the room's first step AND swap the roomscene now
    const handleRoomTabSelect = ({ room, firstIndex }) => {
        dispatch(jumpToStep(firstIndex));

        const matchingRoom = availableRooms.find(
            r => r.area.toLowerCase() === room.toLowerCase()
        );
        if (matchingRoom) {
            const savedSel = sceneSelStrings?.[matchingRoom.area];
            const selToUse = savedSel || matchingRoom.sel;
            dispatch(setActiveScene({ ...matchingRoom, sel: selToUse }));
        }
    };

    // Switch room when step's room changes
    useEffect(() => {
        if (currentStep && availableRooms.length > 0) {
            const stepRoom = currentStep.room;

            // Find the room that matches the step's room area
            const matchingRoom = availableRooms.find(room =>
                room.area.toLowerCase() === stepRoom.toLowerCase()
            );

            if (matchingRoom && (!activeScene || activeScene.area.toLowerCase() !== stepRoom.toLowerCase())) {
                console.log('Step changed - switching to room:', stepRoom);
                // Get the latest saved sel string for this area
                const savedSel = sceneSelStrings?.[matchingRoom.area];
                const selToUse = savedSel || matchingRoom.sel;
                console.log('Step changed - using sel:', selToUse);

                const newScene = { ...matchingRoom, sel: selToUse };
                dispatch(setActiveScene(newScene));
            }
        }
    }, [currentStep, availableRooms, activeScene, sceneSelStrings, dispatch]);

    // Carry over the Kitchen counter selection into any other scene that has a
    // matching counter surface. Runs after each scene "ready" event.
    useEffect(() => {
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) return;

        const handleReady = () => {
            if (!activeScene || activeScene.area?.toLowerCase() === 'kitchen') return;

            // Find the most recent kitchen counter selection. The Counters step
            // now uses per-material stepNames (Counters_granite, Counters_quartz,
            // Counters_quartzite, Counters_marble) so we can't hard-code one key.
            const kitchenCounterEntry = Object.entries(stepSelections || {})
                .filter(([, v]) =>
                    v?.product?.id &&
                    (v.room || '').toLowerCase() === 'kitchen' &&
                    String(v.stepName || '').startsWith('Counters')
                )
                .sort((a, b) => {
                    const ta = new Date(a[1]?.selectedAt || 0).getTime();
                    const tb = new Date(b[1]?.selectedAt || 0).getTime();
                    return tb - ta;
                })[0];

            const kitchenCounter = kitchenCounterEntry?.[1]?.product;
            if (!kitchenCounter?.id) return;

            try {
                chamViz.applyByFilter(
                    kitchenCounter.id,
                    '',
                    function () {
                        const n = String(this.name || '').toLowerCase();
                        if (n.includes('counter')) return kitchenCounter.id;
                        return this.sel;
                    },
                    true
                );
            } catch (err) {
                console.warn('Counter carry-over apply failed:', err);
            }
        };

        chamViz.addEventListener('ready', handleReady);
        return () => chamViz.removeEventListener('ready', handleReady);
    }, [activeScene, stepSelections]);

    // Render the appropriate step component
    const renderStep = () => {
        if (!currentStep) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>No steps available for this elevation.</p>
                </div>
            );
        }

        const { name, params } = currentStep;

        if (name === 'KitchenArrangement') {
            return (
                <KitchenArrangement
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ShowerType') {
            return (
                <ShowerType
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathType') {
            return (
                <BathType
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Floor') {
            return (
                <Floor
                    surfaceName={params.surface || 'Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Counters') {
            return (
                <Counters
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Backsplash') {
            return (
                <Backsplash
                    surfaceName={params.surface || 'Backsplash'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathFloor') {
            return (
                <BathFloor
                    surfaceName={params.surface || 'Bath Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ShowerFloor') {
            return (
                <ShowerFloor
                    surfaceName={params.surface || 'Shower Floor'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'ShowerWall') {
            return (
                <ShowerWall
                    surfaceName={params.surface || 'Shower Wall'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Cabinets') {
            return (
                <Cabinets
                    surfaceName={params.surface || 'Cabinets'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'CabinetHandles') {
            return (
                <CabinetHandles
                    surfaceName={params.surface || 'Cabinet Handles'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'KitchenFaucets') {
            return (
                <KitchenFaucets
                    surfaceName={params.surface || 'Kitchen Faucet'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Mirrors') {
            return (
                <Mirrors
                    surfaceName={params.surface || 'Mirror'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'BathPlumbing') {
            return (
                <BathPlumbing
                    surfaceName={params.surface || 'Bath Plumbing'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        if (name === 'Paint') {
            return (
                <Paint
                    surfaceName={params.surface || 'Walls'}
                    stepIndex={currentStepIndex}
                    room={currentStep.room}
                    onViewAllApplied={handleViewAllApplied}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                />
            );
        }

        return null;
    };

    // Show loading if we don't have the necessary data yet
    if (!floorplan && !persistedFloorplanId) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading visualizer...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Full-width top menu */}
            <header className={styles.topBar}>
                <TopMenu onReviewSelection={() => setIsReviewOpen(true)} />
            </header>

            {/* Body: left column + visualizer */}
            <div className={styles.body}>
                <aside className={styles.leftColumn}>
                    {!showAppliedProducts && <RoomTabs onRoomSelect={handleRoomTabSelect} />}
                    <div className={styles.leftContent}>
                        {showAppliedProducts ? (
                            <AppliedProducts
                                selections={stepSelections}
                                onClose={handleCloseAppliedProducts}
                            />
                        ) : (
                            renderStep()
                        )}
                    </div>
                </aside>

                <main className={styles.centerColumn}>
                    {activeScene && (
                        <Visualizer
                            specifier={activeScene.specifier}
                            sel={activeScene.sel}
                        />
                    )}
                </main>
            </div>

            <ReviewSelectionsModal
                isOpen={isReviewOpen}
                onClose={() => setIsReviewOpen(false)}
                availableRooms={availableRooms}
            />
        </div>
    );
}
